import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NativeSelectProps extends React.ComponentProps<'select'> {
  /** Wrapper class, for grid placement. `className` still dresses the control. */
  containerClassName?: string;
}

/**
 * The platform's own select, wearing the design system's control styles.
 *
 * Forms here are dense and every option list is short, so the native control
 * wins on behaviour: it opens instantly, works with a screen reader out of the
 * box, and on a phone it becomes the OS picker instead of a floating list.
 *
 * What it must not keep is the platform's *look*. Left alone, macOS paints an
 * aqua gradient and a pair of steppers, and the result is a control that shares
 * a form row with our own flat comboboxes and reads as a different product.
 * `appearance-none` plus a chevron of ours settles that: native behaviour,
 * our vocabulary.
 */
export function NativeSelect({ className, containerClassName, ...props }: NativeSelectProps) {
  return (
    <div className={cn('relative w-full', containerClassName)}>
      <select
        className={cn(
          'h-8 w-full appearance-none rounded-md border border-input bg-background pl-2 pr-7 text-sm text-foreground shadow-inner-sm transition-colors duration-150 ease-out',
          'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
          'aria-invalid:border-destructive',
          className,
        )}
        {...props}
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
}
